import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { getTaskLabel, getTaskIcon } from 'app/events/helpers/event-task-helpers';
import { isPastOrTodayLocal } from 'app/events/helpers/is-past-or-today-local';
import { sub } from 'date-fns';
import { CalendarEventActionDetailsFragment, CalendarEventActionPurposeEnum, CalendarEventDetailsFragment, CreateActionForCalendarEventActionGQL } from 'generated/graphql';
import { AddTaskDialog } from '../../dialogs/add-task/add-task.dialog';


// Tooltip text for each task type
const tooltips: Record<CalendarEventActionPurposeEnum, string> = {
  [CalendarEventActionPurposeEnum.Recruit]: 'Create a task to turn out attendees to your event.',
  [CalendarEventActionPurposeEnum.Confirm]: "Create a task to confirm RSVP responses. Will pre-select universe to include any entity with ‘RSVP', ‘confirm’, 'double confirm’, and 'tentative' responses.",
  [CalendarEventActionPurposeEnum.DoubleConfirm]: "Create a task to confirm RSVP statuses, a second time. Will pre-select universe to include any entity with ‘RSVP', ‘confirm’, 'double confirm’, and 'tentative' responses.",
  [CalendarEventActionPurposeEnum.LogAttendance]: "Create a task to confirm attendance at your event. Will pre-select universe to include any entity with ‘RSVP', ‘confirm’, 'double confirm’, and 'tentative' responses."
};

@Component({
  selector: 'app-event-task-manage-box',
  templateUrl: './event-task-manage-box.component.html',
  styleUrls: ['./event-task-manage-box.component.scss']
})

export class EventTaskManageBoxComponent implements OnChanges {
  @Input() eventInfo: CalendarEventDetailsFragment;
  @Input() userCanEditEvent: boolean;

  tooltipText = tooltips;
  orderedTasks: CalendarEventActionDetailsFragment[] = [];

  getTaskDisplayLabel = getTaskLabel;
  getTaskIconClass = getTaskIcon;

  constructor(
    private _router: Router,
    private _dialog: MatDialog,
    private _createActionForCalendarEventActionGQL: CreateActionForCalendarEventActionGQL
    ) {
    }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.eventInfo) {
      this.orderedTasks = this._sortTasks(this.eventInfo.calendarEventActions);
    }
  }


  getTaskTooltipText(task: CalendarEventActionDetailsFragment) {
    const apText = this.tooltipText[task.actionPurpose];

    if (task.actionPurpose === CalendarEventActionPurposeEnum.LogAttendance) {
      return apText;
    }

    if (!this.eventHasPassed()) {
      return apText;
    }

    return `This task is no longer available because the event has passed. ${apText}`;
  }

  canEditOrCreateTask(task: CalendarEventActionDetailsFragment) {
    if (!this.userCanEditEvent) {
      return false;
    }

    if (task.actionPurpose === CalendarEventActionPurposeEnum.LogAttendance) {
      return true;
    }

    return !this.eventHasPassed();
  }

  eventHasPassed() {
    return isPastOrTodayLocal(new Date(this.eventInfo.startDate));
  }

  viewTask(task: CalendarEventActionDetailsFragment) {
    this._router
      .navigate(['/action/edit'], {
        queryParams: {
          campaignId: task.action.campaign.id,
          actionId: task.action.id,
        },
      });
  }

  getScheduledTaskDate(daysBefore: number) {
    return sub(new Date(this.eventInfo.startDate), { days: daysBefore });
  }

  getTaskScheduleStrings(daysBeforeStart: number): string[] {
    const scheduleStrings = [];

    if (daysBeforeStart < 0) {
      scheduleStrings.push(`(${daysBeforeStart * -1} day${daysBeforeStart < 1 ? 's' : ''} after the event starts)`);
    } else if (daysBeforeStart === 0) {
      scheduleStrings.push('(day of event)');
    } else if (daysBeforeStart > 0) {
      scheduleStrings.push(`(${daysBeforeStart} day${daysBeforeStart > 1 ? 's' : ''} before event)`);
    }

    return scheduleStrings;
  }

  openTaskDialog(task: CalendarEventActionDetailsFragment) {
    if (task.actionPurpose === CalendarEventActionPurposeEnum.Recruit) {
      // Immediately create "Recruit" task
      this._createActionForCalendarEventActionGQL
        .mutate({
          input: {
            id: task.id,
          },
        })
        .subscribe(({ data }) => {
          // Redirect to task page
          this._router
            .navigate(['/action/edit'], {
              queryParams: {
                campaignId: this.eventInfo.campaignId,
                actionId: data.createActionForCalendarEventAction.calendarEventAction.action.id,
              },
            });
        });
    } else {
      AddTaskDialog.open(this._dialog, { data: { task } });
    }
  }

  private _sortTasks(tasks: CalendarEventActionDetailsFragment[]) {
    const order = [
      CalendarEventActionPurposeEnum.Recruit,
      CalendarEventActionPurposeEnum.Confirm,
      CalendarEventActionPurposeEnum.DoubleConfirm,
      CalendarEventActionPurposeEnum.LogAttendance,
    ];

    return [...tasks].sort((a, b) => order.indexOf(a.actionPurpose) - order.indexOf(b.actionPurpose));
  }

}
